import {
  GameConfig,
  GameResponse,
  GameEntitlementResponse,
} from '@models/game-config.interface';
import { EconomyResponse, InventoryResponse } from '@models/economy';
import { PlayResponse } from '@models/play-response';
import { SessionParams } from '@models/session-params';
import { Api } from '@services/api/api.interface';
import { MockApiService } from '@services/api/MockApiService';
import { RealApiService } from '@services/api/RealApiService';

/**
 * ApiService factory that returns either MockApiService or RealApiService
 * based on VITE_API_MODE environment variable.
 * Both services are singletons.
 */
export class ApiService implements Api {
  private static mockInstance: Api | null = null;
  private static realInstance: Api | null = null;
  private delegate: Api;

  /**
   * Get the appropriate API service instance based on the environment
   */
  static getInstance(): Api {
    const useMock = import.meta.env.VITE_API_MODE === 'mock';

    if (useMock) {
      if (!ApiService.mockInstance) {
        ApiService.mockInstance = new MockApiService();
      }
      return ApiService.mockInstance;
    } else {
      if (!ApiService.realInstance) {
        ApiService.realInstance = new RealApiService();
      }
      return ApiService.realInstance;
    }
  }

  /**
   * Constructor that delegates to the appropriate singleton instance
   */
  constructor() {
    this.delegate = ApiService.getInstance();
  }

  setToken(token?: string): void {
    this.delegate.setToken(token);
  }

  async fetchGameConfig(url: string, fallbackUrl: string): Promise<GameConfig> {
    return this.delegate.fetchGameConfig(url, fallbackUrl);
  }

  async fetchGameLabels(url: string, fallbackUrl: string): Promise<Record<string, string>> {
    return this.delegate.fetchGameLabels(url, fallbackUrl);
  }

  async fetchGameConfigUrl(params: SessionParams): Promise<GameResponse> {
    return this.delegate.fetchGameConfigUrl(params);
  }

  async play(): Promise<PlayResponse> {
    return this.delegate.play();
  }

  async resetStage(): Promise<void> {
    return this.delegate.resetStage();
  }

  async fetchInventory(): Promise<InventoryResponse> {
    return this.delegate.fetchInventory();
  }

  async fetchEconomy(): Promise<EconomyResponse> {
    return this.delegate.fetchEconomy();
  }

  async fetchGameState(gameUuid: string): Promise<GameEntitlementResponse> {
    return this.delegate.fetchGameState(gameUuid);
  }
}
